import {
  LitElement,
  classMap,
  html,
  unsafeSVG,
  property,
  state,
} from '@oskecare/fe-utilities/lib/LitElement/index.js';
import { bound } from '@oskecare/fe-utilities/lib/decorators';
import deleteIcon from '@oskecare/orange-design-system/src/assets/icons/delete.svg';
import style from './style.scss';
import { MODAL_SIZE_DEFAULT, MODAL_SIZE_LARGE, MODAL_SIZE_SMALL } from './constants.js';

/**
 * Modal component for showing content in fancy window with faded layout over the page
 *
 * Has 3 slots `modal-header`, `modal-body` and `modal-footer`
 * used to inject custom HTML content into the modal window
 */
class Modal extends LitElement {
  /**
   * Unique identifier of the modal window used in HTML markup
   *
   * @type {string}
   */
  @property({ type: String, attribute: 'trigger-id' })
  accessor triggerId = '';

  /**
   * Modal dialog size
   *
   * @type {('small'|'default'|'large')}
   */
  @property({ type: String })
  accessor size = MODAL_SIZE_DEFAULT;

  /**
   * Sticky footer is visible when modal content is too long
   *
   * @type {boolean}
   */
  @property({ type: Boolean, attribute: 'has-sticky-footer' })
  accessor hasStickyFooter = false;

  /**
   * @type {?}
   */
  @state()
  accessor _rovingTabIndexElement = null;

  /**
   * @type {[]}
   */
  @state()
  accessor _pressedDownKeys = [];

  static get styles() {
    return [style];
  }

  /**
   * @returns {boolean}
   */
  get isShiftKeyPressed() {
    return (
      this._pressedDownKeys.includes('ShiftLeft') || this._pressedDownKeys.includes('ShiftRight')
    );
  }

  /**
   * @return {Element[]} - list of focusable elements inside of the modal, featuring modals close button and all focusable elements inside off its slots
   */
  get focusableElements() {
    const modalFocusableElements = this.shadowRoot.querySelectorAll('button');
    const focusableChildren = this.shadowRoot.host.querySelectorAll(
      'button, [href], input, osk-input, osk-autocomplete-input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    return [...modalFocusableElements, ...focusableChildren];
  }

  /**
   * Dispatch `close` event after clicking on the close icon
   *
   * @param {event} event
   */
  @bound
  handleClose(event) {
    event.preventDefault();

    this.dispatchEvent(
      new CustomEvent('close', {
        bubbles: true,
        composed: false,
      }),
    );
  }

  /**
   * When modal is opened and user clicks outside of it
   * dispatch `close` event
   *
   * @param {event} event
   */
  @bound
  handleClick(event) {
    // composedPath is only standard feature able to get the event target nested in one or more shadow DOMs
    // if the browser doesn't support composedPath this won't work
    if (!event.composedPath) {
      return;
    }

    const modalOverlay = this.shadowRoot.querySelector('.modal__overlay');
    const target = event.composedPath().shift();

    if (target === modalOverlay) {
      this.dispatchEvent(
        new CustomEvent('close', {
          bubbles: true,
          composed: false,
        }),
      );
    }
  }

  /**
   * @param {Event} event
   */
  @bound
  handleFocus(event) {
    this._rovingTabIndexElement = event.currentTarget;
  }

  /**
   * Modal keyboard navigation:
   *  - Closes modal after Escape keydown
   *  - Creates focus trap for Tab/Shift+Tab keydown, so customer remains stuck inside of the modal.
   *
   * @param {Event} event
   */
  @bound
  handleKeydown(event) {
    this._pressedDownKeys.push(event.code);
    const activeElementIndex = this.focusableElements.indexOf(this._rovingTabIndexElement);

    switch (event.code) {
      case 'Escape':
        event.preventDefault();
        this.dispatchEvent(
          new CustomEvent('close', {
            bubbles: true,
            composed: false,
          }),
        );
        break;
      case 'Tab':
        if (this.focusableElements.length === 1) {
          event.preventDefault();
          this.focusableElements[0].focus();
        } else if (
          !this.isShiftKeyPressed &&
          activeElementIndex === this.focusableElements.length - 1
        ) {
          event.preventDefault();
          this.focusableElements[0].focus();
        } else if (this.isShiftKeyPressed && activeElementIndex === 0) {
          event.preventDefault();
          this.focusableElements[this.focusableElements.length - 1].focus();
        }
        break;
      default:
        break;
    }
  }

  /**
   * @param {Event} event
   */
  @bound
  handleKeyup(event) {
    this._pressedDownKeys = this._pressedDownKeys.filter(keyCode => keyCode !== event.code);
  }

  /**
   * Lifecycle callback in the custom elements API
   * Fires each time a custom element is appended into a document-connected element
   *
   * Add click event listener to `window` to track click events outside of the modal window
   * Add inline styles and CSS classes to `body` to make it not scrollable
   */
  connectedCallback() {
    if (super.connectedCallback) {
      super.connectedCallback();
    }
    window.addEventListener('click', this.handleClick);
    window.addEventListener('keydown', this.handleKeydown);
    window.addEventListener('keyup', this.handleKeyup);

    const { body } = document;
    const container = document.getElementById('root');

    const scrollTop = document.documentElement.scrollTop || body.scrollTop;
    body.setAttribute('data-lock-scrolltop', scrollTop);

    const bodyStyles = new Map([
      ['height', '100%'],
      ['width', '100%'],
      ['overflow', 'hidden'],
      ['position', 'fixed'],
    ]);
    for (const [prop, value] of bodyStyles.entries()) {
      body.style[prop] = value;
    }

    if (container) {
      container.style.paddingTop = `${container.offsetTop}px`;
      container.style.height = '100%';
      container.style.width = '100%';
      container.style.overflow = 'hidden';
      container.style.position = 'fixed';
      container.style.top = '0';
      container.scrollTop = scrollTop;
    }

    body.classList.add('has-modal');

    window.requestAnimationFrame(() => {
      window.scrollTo(0, scrollTop);
    });
  }

  /**
   * Lifecycle callback in the custom elements API
   * Fires each time a custom element is removed from a document-connected element
   *
   * Remove event listeners form the `window` as well as inline styles
   * and CSS classes form the `body`
   */
  disconnectedCallback() {
    const { body } = document;

    const scrollTop = body.getAttribute('data-lock-scrolltop');

    const bodyStyles = ['height', 'width', 'overflow', 'position'];
    bodyStyles.forEach(prop => {
      body.style[prop] = null;
    });

    const container = document.getElementById('root');

    body.classList.remove('has-modal');

    if (container) {
      container.style.height = '';
      container.style.width = '';
      container.style.overflow = '';
      container.style.position = '';
      container.style.top = '';
      container.style.paddingTop = '';
    }

    window.requestAnimationFrame(() => {
      window.scrollTo(0, scrollTop);
    });

    this.focusableElements.forEach(focusableElement => {
      focusableElement.removeEventListener('focus', this.handleFocus);
    });
    window.removeEventListener('click', this.handleClick);
    window.removeEventListener('keydown', this.handleKeydown);
    window.removeEventListener('keyup', this.handleKeyup);
    if (super.disconnectedCallback) {
      super.disconnectedCallback();
    }
  }

  /**
   * Lifecycle function.
   * Called after the element’s DOM has been updated the first time, immediately before `updated` is called.
   *
   * Sets focus event listeners (needs to be done here instead of connectedCallback, since slots are not yet populated there).
   * Sets focus inside of the modal on its header element.
   * Also sets is-active class to root div element to trigger css opacity transition bound to it.
   */
  firstUpdated() {
    super.firstUpdated();
    this.focusableElements.forEach(focusableElement => {
      focusableElement.addEventListener('focus', this.handleFocus);
    });
    const initialFocusElement = this.shadowRoot.host.querySelector(`[slot=modal-header]`);
    initialFocusElement.setAttribute('tabindex', '-1');
    initialFocusElement.focus();

    const rootElement = this.shadowRoot.querySelector('div.modal');
    rootElement.classList.add('is-active');
  }

  render() {
    return html`
      <div
        id=${this.triggerId}
        data-modal
        class="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="${this.triggerId}-title"
      >
        <div class="modal__overlay" tabindex="-1"></div>
        <div
          role="document"
          class="modal__dialog ${classMap({
            'modal__dialog--small': this.size === MODAL_SIZE_SMALL,
            'modal__dialog--large': this.size === MODAL_SIZE_LARGE,
          })}"
        >
          <div class="modal__header">
            <button
              type="button"
              class="btn btn--square btn--small btn--ghost modal__close"
              aria-label="Zatvoriť"
              @click=${this.handleClose}
            >
              <svg class="icon icon--small icon--delete" role="presentation" aria-hidden="true">
                ${unsafeSVG(deleteIcon)}
              </svg>
              <span class="visually-hidden"></span>
            </button>
          </div>

          <div class="modal__body">
            <slot name="modal-header" id="${this.triggerId}-title"></slot>
            <slot name="modal-body"></slot>
          </div>
          <div class="modal__footer ${classMap({ 'modal__footer--sticky': this.hasStickyFooter })}">
            <slot name="modal-footer"></slot>
          </div>
        </div>
      </div>
    `;
  }
}

// In case of duplicate custom element registration due to multiple
// instances of the element used, we want to skip the second registration causing error.
if (!customElements.get('osk-modal')) {
  customElements.define('osk-modal', Modal);
}
